import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared page used for both `/reset-password?token=…` (forgot flow) and
// `/set-password?token=…` (auto-created seeker sets their first password).
// Both call the same backend endpoint; only the copy differs.
export default function ResetPasswordPage({ mode = 'reset' }) {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const copy = mode === 'set-password'
        ? {
            heading: 'Set your password',
            intro: 'Welcome to Methode 4 Life. Choose a password to open your profile and access what you bought. Minimum 8 characters.',
            cta: 'Save my password',
            done_h: 'Password saved',
            done_p: 'You can now sign in with the email that received the welcome message.',
        }
        : {
            heading: 'Choose a new password',
            intro: 'Enter a new password for your Methode 4 Life account. Minimum 8 characters.',
            cta: 'Save new password',
            done_h: 'Password updated',
            done_p: 'You can now sign in with your new password.',
        };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) return toast.error('Password must be at least 8 characters.');
        if (password !== confirm) return toast.error('Passwords do not match.');
        setLoading(true);
        try {
            await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
            setDone(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'This link is no longer valid.');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-[#F9F6F0]">
                <Navbar />
                <main className="pt-24 pb-16">
                    <div className="max-w-md mx-auto px-6 sm:px-8 text-center">
                        <p className="body-text mb-6">This link is missing its token. Please use the button from the email you received.</p>
                        <Link to="/login" className="btn-primary inline-flex" data-testid="reset-no-token-login">Back to sign in</Link>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />
            <main className="pt-24 pb-16" data-testid={`${mode}-page`}>
                <div className="max-w-md mx-auto px-6 sm:px-8">
                    <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#5B2D8E] hover:underline mb-8">
                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                    </Link>
                    <p className="overline mb-3">{mode === 'set-password' ? 'Welcome' : 'Password reset'}</p>
                    <h1 className="heading-2 mb-4">{copy.heading}</h1>
                    <p className="body-text mb-8">{copy.intro}</p>

                    {done ? (
                        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="reset-password-done">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-green-600 mt-0.5" />
                                <div>
                                    <p className="font-serif text-lg text-[#1A1A2E] mb-1">{copy.done_h}</p>
                                    <p className="text-sm text-[#4A4A68]">{copy.done_p} Redirecting…</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="space-y-4 bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="reset-password-form">
                            <div>
                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">New password</label>
                                <div className="relative">
                                    <input
                                        type={show ? 'text' : 'password'}
                                        required
                                        minLength={8}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full pr-10"
                                        data-testid="reset-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShow(!show)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A4A68]"
                                        tabIndex={-1}
                                    >
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Confirm password</label>
                                <input
                                    type={show ? 'text' : 'password'}
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="w-full"
                                    data-testid="reset-password-confirm"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full"
                                data-testid="reset-password-submit"
                            >
                                {loading ? 'Saving…' : copy.cta}
                            </button>
                        </form>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
